import { useState, useEffect, type FormEvent } from "react";
import { useToast, useColorModeValue } from "@chakra-ui/react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Button, FormControl, FormLabel, Input, VStack, SimpleGrid, Checkbox, Box, Text,
} from "@chakra-ui/react";
import { adminApi } from "../../../data/axios/adminApi";
import { AdminRole, AdminPermission } from "../../../types/admin";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  role: AdminRole | null;
  allPermissions: AdminPermission[];
  onSuccess: () => void;
}

export function RoleFormModal({ isOpen, onClose, role, allPermissions, onSuccess }: Props) {
  const toast = useToast();

  const titleColor    = useColorModeValue("#434347", "#e8edf2");
  const labelColor    = useColorModeValue("#434347", "#e8edf2");
  const cardBorder    = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.1)");
  const inputBg       = useColorModeValue("white", "rgba(255,255,255,0.05)");
  const inputBorder   = useColorModeValue("rgba(0,0,0,0.15)", "rgba(255,255,255,0.15)");
  const categoryBg    = useColorModeValue("rgba(0,158,251,0.03)", "rgba(0,158,251,0.05)");
  const categoryBorder = useColorModeValue("rgba(0,0,0,0.08)", "rgba(255,255,255,0.08)");
  const categoryLabel = useColorModeValue("#84858c", "#718096");

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<number[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (role) {
      setName(role.name);
      setDescription(role.description || "");
      setSelectedPermissionIds(role.permissions.map(p => p.id));
    } else {
      setName("");
      setDescription("");
      setSelectedPermissionIds([]);
    }
  }, [role, isOpen]);

  const groupedPermissions = allPermissions.reduce((acc, perm) => {
    const category = perm.code.split(".")[0] || "outros";
    if (!acc[category]) acc[category] = [];
    acc[category].push(perm);
    return acc;
  }, {} as Record<string, AdminPermission[]>);

  const handleCheckboxChange = (id: number, isChecked: boolean) => {
    setSelectedPermissionIds((prev) =>
      isChecked ? [...prev, id] : prev.filter((pid) => pid !== id),
    );
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name) return toast({ title: "O nome é obrigatório", status: "warning", duration: 3000, isClosable: true });

    try {
      setSubmitting(true);
      let currentRole = role;

      if (currentRole) {
        currentRole = await adminApi.updateRole(currentRole.id, { name, description });
      } else {
        currentRole = await adminApi.createRole({ name, description });
      }

      await adminApi.setRolePermissions(currentRole.id, selectedPermissionIds);

      onSuccess();
      onClose();
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast({ title: detail || "Erro ao salvar papel", status: "error", duration: 5000, isClosable: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit} borderRadius="2xl" mx={4}>
        <ModalHeader
          fontSize="lg"
          fontWeight={600}
          color={titleColor}
          borderBottomWidth="1px"
          borderColor={cardBorder}
          pb={4}
        >
          {role ? "Editar Papel" : "Novo Papel"}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody maxH="60vh" overflowY="auto" pt={5}>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={1}>Nome do Papel</FormLabel>
              <Input
                placeholder="Ex: admin, rh, coordenador"
                value={name}
                onChange={(e) => setName(e.target.value)}
                bg={inputBg}
                borderColor={inputBorder}
                borderRadius="8px"
                fontSize="sm"
                _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight={600} color={labelColor} mb={1}>Descrição</FormLabel>
              <Input
                placeholder="O que as pessoas com esse papel fazem..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                bg={inputBg}
                borderColor={inputBorder}
                borderRadius="8px"
                fontSize="sm"
                _focus={{ borderColor: "#009efb", boxShadow: "0 0 0 2px rgba(0,158,251,0.15)" }}
              />
            </FormControl>

            {Object.keys(groupedPermissions).length > 0 && (
              <>
                <Text fontWeight={600} fontSize="sm" color={titleColor} mt={2}>Atribuir Permissões</Text>

                {Object.entries(groupedPermissions).map(([category, perms]) => (
                  <Box
                    key={category}
                    p={4}
                    bg={categoryBg}
                    borderRadius="12px"
                    borderWidth="1px"
                    borderColor={categoryBorder}
                  >
                    <Text
                      textTransform="uppercase"
                      fontSize="10px"
                      fontWeight={700}
                      color={categoryLabel}
                      letterSpacing="0.5px"
                      mb={3}
                    >
                      {category}
                    </Text>
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2.5}>
                      {perms.map(perm => (
                        <Checkbox
                          key={perm.id}
                          isChecked={selectedPermissionIds.includes(perm.id)}
                          onChange={(e) => handleCheckboxChange(perm.id, e.target.checked)}
                          colorScheme="blue"
                          size="sm"
                        >
                          <Text fontSize="xs" color={labelColor} fontFamily="mono">{perm.code}</Text>
                        </Checkbox>
                      ))}
                    </SimpleGrid>
                  </Box>
                ))}
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" borderColor={cardBorder} gap={2} pt={4}>
          <Button variant="ghost" borderRadius="50px" size="sm" onClick={onClose}>Cancelar</Button>
          <Button
            bg="#009efb"
            color="white"
            _hover={{ bg: "#007bd4" }}
            borderRadius="50px"
            size="sm"
            type="submit"
            isLoading={submitting}
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
